/**
 * How a line's description is composed from its product and its cities, in one
 * place — and pinned, byte for byte, against a string another system composes
 * independently.
 *
 * ## ⚠️ THIS IS A JOIN KEY, NOT FORMATTING
 *
 * Read that again before editing anything below. Everything in this file looks
 * like presentation and none of it is.
 *
 * A quote or invoice line has a `description` column that is `NULL` until an
 * operator types into it. While it is `NULL`, **two systems compose that
 * sentence separately and neither ever compares its answer with the other**:
 *
 * - **EMERGE** composes it here, to show the operator what the line says. It is
 *   a preview — nothing sends it anywhere.
 * - **The backend** composes it again at push time, in
 *   `xero-quote.payload.ts` / `xero-invoice.payload.ts` (`lineDescription`),
 *   and **that** is the string printed on the customer's Xero document.
 *
 * ```ts
 * // emerge-backend-app — the string this file must reproduce exactly.
 * // `lineDescription` in xero-quote.payload.ts, IMPORTED WHOLE by
 * // xero-invoice.payload.ts so the two document types cannot diverge.
 * const projectName = line.project?.name.trim() ?? '';
 * const itemCode    = line.productCode?.trim()  ?? '';
 * const head = projectName !== '' && itemCode !== '' ? projectName : line.productName;
 *
 * // geographySegment — CITIES ELSE REGIONS, one segment, never both.
 * const names = line.cityNames.length > 0 ? line.cityNames : line.regionNames;
 * const geography = names.length === 0 ? '' : ` — ${names.join(', ')}`;
 *
 * // inclusionsSegment — omitted entirely when the line promises nothing.
 * const inclusions =
 *   line.inclusions.length === 0 ? '' : ` (${line.inclusions.map(inclusionText).join('; ')})`;
 *
 * `${head}${geography}${inclusions}`
 * ```
 *
 * So the failure mode is not a wrong-looking preview. Handed over verbatim by
 * the backend when they gave us this risk:
 *
 * > "If your joiner ever differs from ours, a line reads one way in EMERGE and
 * > another in Xero, and nothing on either side will say so. The operator sees
 * > your version, accepts it as correct, never types anything, and the document
 * > says something else."
 *
 * **Nothing reports this.** Not `tsc`, not oxlint, not a test, not the server.
 * Both strings are individually well-formed; they simply disagree, on a
 * document a customer is invoiced against. The only symptom is a customer
 * reading something the operator never saw.
 *
 * ### What "changing it" includes
 *
 * All of these are breaking, and every one of them looks like a tidy-up in a
 * diff:
 *
 * - swapping the EM DASH for a hyphen `-` or an en dash `–`
 * - closing the spaces around it (`A—B`)
 * - tightening the city joiner from `, ` to `,`
 * - adding an Oxford `and` before the last city
 * - trimming, case-normalising, sorting or deduplicating the city names
 * - emitting the separator when the city list is empty
 * - changing what counts as a **present item code** — see
 *   {@link hasXeroItemCode}. That predicate is a join key too, for the same
 *   reason and with the same invisibility: it decides *which name heads the
 *   sentence*, so if the two sides disagree about whether `'  '` is a code, they
 *   disagree about the whole head on that line.
 * - **removing the `.trim()` on the project name** — see `usableProjectName`.
 *   The backend trims; a raw emit here differs by whitespace on the identical
 *   branch, which is the quietest divergence in this file and the one that has
 *   actually happened.
 *
 * **If you change any of it, tell the emerge-backend-app peer in the same
 * breath** — the two sides have to move together, and there is no build that
 * will notice if they do not.
 *
 * ### This repo has done this before, and the precedent is named
 *
 * The region label **`Middle East & North Africa`** must not be shortened to
 * `MENA`. It reads like a label; it is matched against Xero's own tracking
 * list, and abbreviating it reintroduces `XERO_TRACKING_LOCATION_UNKNOWN` for
 * every MENA quote. Migration 062 renamed it *back* for exactly that reason.
 * Same class of hazard, same invisibility: a string that looks like copy and is
 * really a join against somebody else's system.
 *
 * ## ⚠️ The project-name substitution is LIVE on both sides
 *
 * The head of the sentence becomes the line's **project** name when the line
 * names one — but **only when the line also carries a Xero item code**. That
 * rule is implemented below, guard included, behind
 * `LINE_DESCRIPTION_PREFERS_PROJECT_NAME`, which is **`true`**. The backend has
 * deployed its half.
 *
 * Read that constant's own comment before touching it. In particular: `false`
 * is no longer the safe default — the backend leads with the project name in
 * production, so reverting here is itself a one-sided change.
 *
 * ⚠️ **There are THREE join keys, not one.** The separator, the **item-code
 * predicate** ({@link hasXeroItemCode}) and the **project-name trim**
 * (`usableProjectName`) all have to agree with the backend's own logic. The
 * latter two are the ones liable to be "simplified" — the first by someone who
 * reads it as a null check, the second by someone who reads a `.trim()` as
 * cosmetic. If the two sides ever test the code differently the guard opens on
 * one side and not the other; if they ever disagree about the trim they take
 * the identical branch and still emit different strings. Both put us straight
 * back to two systems composing one sentence.
 *
 * ## Why the separator is written as an escape
 *
 * `\u2014` rather than a literal `—`. At a glance, in a proportional editor
 * font, an EM DASH (`—`, U+2014), an EN DASH (`–`, U+2013) and a HYPHEN-MINUS
 * (`-`, U+002D) are near enough indistinguishable — and a stray non-breaking
 * space around one is invisible outright. This single character is what the
 * whole join hangs on, so it is spelled by codepoint, where a wrong one is
 * legible as a wrong one. Verified on the wire against both files: the bytes
 * are `20 e2 80 94 20`.
 *
 * ## Campaign inclusions: DECIDED, BUILT, AND **LIVE**
 *
 * The composition is written in full below and is **ON** —
 * {@link LINE_DESCRIPTION_INCLUDES_INCLUSIONS} is `true`, so an inclusion set
 * reaches the customer's document.
 *
 * Both questions this file once turned on are settled, in the direction the
 * code already took:
 *
 * 1. **Do inclusions belong on the customer's document at all?** — **YES.**
 * 2. **In what form?** — **BARE BRACKETS**, no label:
 *    `(Campus Outreach: 2 campuses)`, *not* `(Includes: Campus Outreach: 2
 *    campuses)`. That is exactly the shape
 *    {@link LINE_DESCRIPTION_INCLUSIONS_OPEN} emits.
 *
 * ✅ **THE BLOCKER IS GONE ON BOTH DOCUMENT TYPES — measured 2026-08-31.**
 * *SUPERSEDED: "the single remaining blocker is a SCHEMA GAP IN THE BACKEND …
 * `QuoteLineInput` / `InvoiceLineInput` … carry no inclusions at all … their
 * `lineDescription` has nothing to compose from."* Both line-input types carry
 * `inclusions` now, and `xero-invoice.payload.ts` composes the segment by
 * **importing `inclusionsSegment` from `xero-quote.payload.ts`** rather than
 * holding a second copy — so the two document types cannot word it differently.
 *
 * ⚠️ **That superseded paragraph was RIGHT when written and stayed on the page
 * after the schema moved, in the file whose whole subject is a claim about
 * somebody else's repo.** Nothing here could see it change: no build, no lint,
 * no type. The half that outlives it is the BAR — a flip is justified by the
 * backend being **deployed and answering on the wire**, never by a merge, a
 * commit or a working tree — and that bar is exactly what re-measuring the
 * backend's source satisfied here.
 *
 * The design decisions above are untouched by any of it: bare brackets, no
 * label, and the wording is `inclusions.ts`'s. A sentence about *what this
 * should say* was settled by the owner and must not be re-litigated.
 *
 * ⚠️ **A FOURTH join key arrives with it, and it is in another file.** The
 * per-inclusion wording — `` `${name}: ${quantity} ${unit}` ``, with the unit
 * chosen by `quantity === 1` and the number DROPPED entirely when that unit is
 * `null` — lives in `src/lib/inclusions.ts` and is deliberately not copied
 * here. That module was written as a rendering rule for four UI surfaces; the
 * moment this constant goes `true` it becomes a customer-document join key with
 * a second implementation in another repo.
 *
 * `inclusions.ts` carries the failure mode, the list of edits that break it
 * (the `unitSingular ?? unitPlural` fallback foremost, because it is the
 * tempting one and it prints `1 campuses`), and the reason its two stored
 * columns are what make agreement between two independent implementations
 * possible at all.
 */

/*
 * ⚠️ The only imports this module has, and both are pinned rules rather than
 * helpers. `renderInclusion` is the ONE copy of the singular/plural wording —
 * copying it in here would be the second copy `inclusions.ts` was written to
 * prevent — and `isValidInclusionQuantity` is the ONE copy of "what the server
 * will accept". Neither pulls in React, a hook or a fetch, so this module stays
 * pure and reasonable about without a browser or a live Xero tenant.
 */
import { isValidInclusionQuantity, renderInclusion } from './inclusions';
import type { LineInclusion } from './types';

/**
 * What sits between the product name and the city list: **space, EM DASH
 * (U+2014), space**.
 *
 * Exported so a call site can *match* on it — splitting a stored description to
 * ask whether it still equals the derivation, say — rather than writing a
 * second copy of the character and starting the divergence here.
 */
export const LINE_DESCRIPTION_SEPARATOR = ' \u2014 ';

/**
 * What sits between two city names — **and between two REGION names**: **comma,
 * space**.
 *
 * `Array.prototype.join` with no argument gives `,` with no space, which is why
 * this is stated rather than defaulted.
 *
 * ⚠️ **Regions join the SAME way cities do, on purpose, and the name of this
 * constant understates that.** They occupy the same slot in the sentence — see
 * `geographySegment` — so a second joiner would be a second thing for the two
 * sides to disagree about, for no gain. The backend states the same rule in the
 * same words at its own `geographySegment`. It is not renamed to
 * `…_GEOGRAPHY_JOINER` because the export is cited by name across two
 * repositories; read the name as historical and the doc as the rule.
 */
export const LINE_DESCRIPTION_CITY_JOINER = ', ';

/**
 * What OPENS the campaign-inclusions segment: **space, LEFT PARENTHESIS**.
 *
 * # ⚠️ DECIDED: bare brackets, and NO LABEL
 *
 * The customer's document reads **`(Campus Outreach: 2 campuses)`** — a bare
 * bracketed list — never a labelled one,
 * **`(Includes: Campus Outreach: 2 campuses)`**.
 *
 * ⚠️ **So a label is a REVERSAL, not an improvement.** `Includes: ` reads
 * like the obvious missing word — it is the first thing anyone adds when they
 * meet a bare bracket on an invoice — and adding it would both overturn a
 * decision the owner took and, once
 * {@link LINE_DESCRIPTION_INCLUDES_INCLUSIONS} is `true`, diverge from the
 * backend on every line carrying an inclusion. The same goes for any other
 * prefix: `Incl.`, `With`, `Campaign inclusions:`. There is no label.
 *
 * The reasoning below is about the BRACKET — why parentheses at all, rather than
 * a second em dash or a newline — and is unaffected by that decision.
 *
 * # ⚠️ NOT a second em dash, and that is the whole design of this segment
 *
 * The obvious move is to reuse {@link LINE_DESCRIPTION_SEPARATOR} and append a
 * third peer segment — `Head — Cities — Inclusions`. It must not be done, for a
 * reason that is structural rather than typographic:
 *
 * - **The em dash currently means exactly one thing: "the city list starts
 *   here."** It is exported so a call site can SPLIT a stored description and
 *   ask whether it still equals the derivation. A second use makes that split
 *   ambiguous — given `A — B — C` nothing can say whether `B` is a city list or
 *   an inclusion list.
 * - **The ambiguity is total on a line with inclusions and no cities.**
 *   `Virtual Masterclasses — Campus Outreach: 2 campuses` is byte-identical to
 *   the same line running in a city that happens to be named that. Two
 *   different documents, one string, nothing able to tell them apart.
 *
 * Parentheses were chosen over the other candidates deliberately:
 *
 * - a **newline** was rejected outright. It reads best on the document and it
 *   is the worst possible join key here: the derived text pre-fills a
 *   `<textarea>`, and the browser's own form-submission normalisation turns
 *   `\n` into `\r\n`. A one-byte, invisible, per-browser divergence on a string
 *   nothing compares is precisely this file's failure mode. This repo already
 *   has mixed CRLF/LF with no `.gitattributes`; it does not need it on the wire.
 * - a **bullet** `•` or a **pipe** `|` reads as a third peer segment, which
 *   is what the em dash reuse got wrong — an inclusion list is subordinate to
 *   the line, not beside it.
 * - a **semicolon** with no bracket cannot say where the list ENDS, which
 *   matters the moment anything is ever appended after it.
 *
 * A parenthesis is unmistakable in every editor font — the lookalike problem
 * the em dash has does not exist for it — so this one is written literally
 * rather than as an escape.
 *
 * ⚠️ LIVE — {@link LINE_DESCRIPTION_INCLUDES_INCLUSIONS} is `true`.
 */
export const LINE_DESCRIPTION_INCLUSIONS_OPEN = ' (';

/** What CLOSES the campaign-inclusions segment. See {@link LINE_DESCRIPTION_INCLUSIONS_OPEN}. */
export const LINE_DESCRIPTION_INCLUSIONS_CLOSE = ')';

/**
 * What sits between two inclusions: **semicolon, space**.
 *
 * ⚠️ **Not `', '`, and the reason is that an inclusion already contains a
 * comma-free colon pair.** `Campus Outreach: 2 campuses` is one item; joined
 * with `', '` a three-inclusion list reads
 * `Campus Outreach: 2 campuses, Network Partner: 1 partner, Priority Support`,
 * where the commas are doing the same job as the ones separating the CITY names
 * four characters earlier and mean something different. The semicolon is the
 * one separator that outranks a colon in ordinary punctuation, so the nesting
 * reads correctly with no bracket per item.
 *
 * ⚠️ LIVE — {@link LINE_DESCRIPTION_INCLUDES_INCLUSIONS} is `true`.
 */
export const LINE_DESCRIPTION_INCLUSION_JOINER = '; ';

/**
 * Whether a line carries a **Xero item code** — the test that opens the
 * project-name substitution.
 *
 * # ⚠️ PINNED: non-empty after `trim()`. Agreed with the backend.
 *
 * ```ts
 * productCode != null && productCode.trim() !== ''
 * ```
 *
 * `null`, `undefined`, `''` **and whitespace-only all count as ABSENT.** That
 * last clause is the whole reason this is written down: `''` and `'   '` are the
 * values a `!= null` test gets wrong, and both are exactly what a free-text
 * `product_code` column produces when somebody clears the box or types a space.
 * A backend testing `if (productCode)` agrees with this by luck on `''` and
 * **disagrees on `'   '`**; one testing `productCode !== null` disagrees on both.
 *
 * ## Why this exists at all — the guard, and what it protects
 *
 * The Xero line carries **`ItemCode`**, set from the product's `productCode`
 * (`xero-quote.payload.ts:923`), and on the **invoice** side it is REQUIRED —
 * `assertLineItemCode` refuses the push without one. So on a line that has a
 * code, the product's identity survives on the customer's document *independently
 * of the description text*, and replacing the product name with the project name
 * in that sentence loses nothing. That is what makes the owner's "instead of"
 * acceptable rather than lossy.
 *
 * ⚠️ **Without the guard there is a hole, and it is a correlation that closes it
 * today, not a constraint.** The three products with no `product_code` are
 * `Meet Up`, `Profile Evaluations` and `Miscellaneous` — which are precisely the
 * products that have no projects and, per the owner, never will. So an unguarded
 * rule looks safe *on today's data*. Nothing stops someone creating a project for
 * a codeless product tomorrow: that line would then name **neither** the
 * project's product **nor** an item code, and the customer would receive a line
 * saying only where and when — no indication of what was sold, anywhere on it.
 * The guard closes that, and it costs nothing on any line that has a code.
 *
 * ## Same register as the separator and the joiner
 *
 * Exported for the reason {@link LINE_DESCRIPTION_SEPARATOR} is: so a call site
 * can ask the *same* question rather than writing a second copy of the test and
 * starting the divergence here. A hand-written `productCode !== null` three files
 * away is this file's rule, forked, with nothing to notice.
 */
export function hasXeroItemCode(productCode: string | null | undefined): boolean {
  if (productCode === null || productCode === undefined) return false;
  return productCode.trim() !== '';
}

/**
 * Whether the composed description leads with the line's **project** name rather
 * than its **product** name, on a line that names a project **and** carries a
 * Xero item code.
 *
 * # ⚠️ THIS IS `true` — LIVE, AND PINNED TO A DEPLOYED BACKEND
 *
 * The rule: *"if a project is selected then fetch the project name instead of
 * the product name in the description of the quote line item"*. It is built in
 * full in `composeLineDescription` below and is **on** — the backend has
 * deployed the matching half, so both sides lead with the project name under
 * the same guarded rule.
 *
 * ## The three things the two composers are pinned on
 *
 * This did not merely turn a feature on: it added two more join keys beside
 * the separator. All three must move together or not at all, and none of them
 * is checked by anything at runtime:
 *
 * 1. **The branch rule.** Prefer the project name only when the line names a
 *    project **and** carries a Xero item code; otherwise the product name. The
 *    backend's deployed shape, verbatim:
 *
 *    ```ts
 *    // emerge-backend-app
 *    const projectName = line.project?.name.trim() ?? '';
 *    const itemCode    = line.productCode?.trim()  ?? '';
 *    const head = projectName !== '' && itemCode !== '' ? projectName : line.productName;
 *    ```
 *
 * 2. **The item-code predicate** — non-empty after `trim()`, so `''` and `'   '`
 *    both count as ABSENT. Pinned on {@link hasXeroItemCode}; the backend's
 *    `line.productCode?.trim() ?? ''` asks exactly that question.
 * 3. **The trim.** The project name is emitted **trimmed** on both sides.
 *    Agreeing on the branch is not agreeing on the string: two implementations
 *    can take the identical branch and still differ by two spaces.
 *
 * The fixture table at the bottom of this file is the shared pin for all three.
 * It states the agreed answer per input rather than describing the rule in
 * prose twice, and the backend asserts their composer against the same pairs —
 * two independent pins become one shared one.
 *
 * ## ⚠️ The rule is GUARDED — do not remove the guard
 *
 * The obvious simplification is to prefer the project name whenever there is
 * one. **That is not what ships.** The rule is:
 *
 * > **Prefer the project name ONLY when the line also carries a Xero item code.
 * > Otherwise keep the product name.**
 *
 * The reasoning is on {@link hasXeroItemCode} and is short: the item code
 * carries the product's identity onto the customer's document independently of
 * this sentence — it is REQUIRED on the invoice push — so on a coded line the
 * substitution loses nothing, and on an uncoded line it would leave a
 * customer-facing line naming neither the product nor a code. The three
 * codeless products happen to be the three that have no projects today; that
 * is a correlation in current data, not a constraint, and the guard is what
 * stops tomorrow's data breaking it.
 *
 * ⚠️ **Shipping unguarded on one side and guarded on the other makes every
 * codeless line with a project disagree** — one side the product, the other
 * the project — which is the same silent divergence as flipping early, narrowed
 * to a subset of lines and therefore *harder* to notice, not easier.
 *
 * ## ⚠️ The reverse is now the dangerous edit
 *
 * Setting this back to `false` is no longer "returning to the safe default" — the
 * backend leads with the project name in production, so a `false` here puts the
 * two sides straight into the divergence below with the roles swapped:
 *
 * ```
 * operator sees (here)   Sales - Business School Festivals — Mumbai
 * customer receives      BSF India - Summer 2027 — Mumbai
 * ```
 *
 * Both strings are well-formed. `tsc` is green, oxlint is green, the push returns
 * 200, the document looks finished, and **nothing on either side compares them**.
 * The operator reads our version, accepts it, never types a description, and the
 * customer is invoiced against a sentence nobody saw. Same family as the
 * `taxRate` / `xeroTaxType` divergence — one value feeding two systems with no
 * reconciliation — which shipped, rendered perfectly, and was found only by
 * someone screenshotting a live row. **If this has to go back to `false`, the
 * backend reverts in the same breath, and theirs goes first.**
 *
 * ## ⚠️ `productCode` is still an OPTIONAL parameter, and an omitted one is silent
 *
 * See {@link LineDescriptionInput}. **A caller that omits it reads as "no item
 * code", so the guard stays shut and the line keeps the product name** — while the
 * backend, reading the line's real code, leads with the project name. A missing
 * argument is therefore now a *divergence*, not a graceful degradation, and it
 * cannot be seen from the output: the string is well-formed and simply names the
 * wrong thing.
 *
 * `descriptionOf` in `QuoteLineItemsTable` is the only call site today and it
 * passes all four fields. **Any new call site must pass `productCode`, resolved
 * to the product's real code rather than `undefined`.** It is optional only
 * because widening it to required is a breaking signature change; treat it as
 * required.
 *
 * ## It is a SOURCE CONSTANT, not a feature flag
 *
 * Deliberately not an env var, not a runtime toggle, not a prop, not a parameter
 * and not anything a user or a deploy can move. It marks a **coordination point
 * between two repositories**, and the only correct way to change it is a
 * one-line edit here, made by someone who has verified the condition above.
 * Threading it outward would let a call site pick a variant — which is precisely
 * how a pinned join stops being pinned.
 */
/*
 * ⚠️ **The branch and the trim are two independent join keys, and both must
 * move together.** Agreeing on which branch to take (project name vs. product
 * name) is not the same as agreeing on the emitted string — a trimmed composer
 * under `false`, or an untrimmed one under `true`, each puts the preview and
 * the customer's document into disagreement, silently, in a way no build or
 * lint can see. If either half is ever reverted, revert both.
 */
export const LINE_DESCRIPTION_PREFERS_PROJECT_NAME = true;

/**
 * Whether the composed description also lists the line's **campaign
 * inclusions**, after the cities.
 *
 * # ⚠️ `true` — LIVE ON QUOTES **AND** INVOICES
 *
 * The composition is built in full in {@link composeLineDescription} and is
 * **on**: `(Campus Outreach: 2 campuses)`, bare brackets, no label. The bracket
 * form is {@link LINE_DESCRIPTION_INCLUSIONS_OPEN}'s and the wording is
 * `renderInclusion`'s; the `I`-numbered fixture rows below state the agreed
 * output for every case.
 *
 * 🔴 **THE QUOTE/INVOICE ASYMMETRY IS GONE — owner's reversal, backend commit
 * `3cd2e1c`.** *SUPERSEDED: "⚠️ **The invoice builder deliberately omits the
 * segment.** Convert copies a NULL description, so **the same sale reads WITH
 * inclusions on the quote and WITHOUT on the invoice.** That asymmetry is a
 * decision, not drift — do not 'harmonise' it."* `xero-invoice.payload.ts`
 * **imports `inclusionsSegment`** from the quote builder, so convert copying a
 * NULL description now yields the identical sentence on both documents.
 *
 * ⚠️ **The struck sentence forbade by name the exact edit that later became
 * correct**, which is why it is struck rather than deleted: a reader who met it
 * after the reversal was told, in bold, not to do the right thing — and it was
 * quoted verbatim into two grid files, so the prohibition outlived its subject
 * in three places at once. **Copy of a decision travels; a correction to it does
 * not.** When a rule is quoted into call sites, the call sites are part of the
 * edit.
 *
 * ⚠️ **What is NOT pinned by anything executable.** The two composers (this one
 * and the backend's) agree by prose and a shared fixture table, and nothing at
 * runtime compares them. If this ever needs to go back to `false`, the backend
 * reverts in the same breath.
 *
 * ## What being wrong here costs, stated concretely
 *
 * ```
 * operator sees (here)   Virtual Masterclasses — Mumbai (Campus Outreach: 2 campuses)
 * customer receives      Virtual Masterclasses — Mumbai
 * ```
 *
 * Both are well-formed. `tsc` is green, oxlint is green, the push returns 200,
 * and **nothing on either side compares them.** The operator sees the
 * inclusions written out, takes the line as correct, never types a description
 * — and the customer is invoiced against a document that does not mention what
 * they were promised.
 *
 * ⚠️ **This is unobservable on today's data.** Production carries very few
 * lines with any inclusion at all, so a divergence here would look completely
 * correct on every other line in the system.
 *
 * ## It is a SOURCE CONSTANT, not a feature flag
 *
 * The same reasoning as {@link LINE_DESCRIPTION_PREFERS_PROJECT_NAME}: not an
 * env var, not a prop, not a parameter, not anything a call site or a deploy
 * can move. It marks a coordination point between two repositories, and
 * threading it outward would let a call site pick a variant — which is exactly
 * how a pinned join stops being pinned.
 */
export const LINE_DESCRIPTION_INCLUDES_INCLUSIONS = true;

/**
 * The four facts about ONE campaign inclusion that its wording needs.
 *
 * A `Pick` of {@link LineInclusion} rather than a fresh interface, so a call
 * site can hand a read entry straight in and so a shape change upstream lands
 * here as a compile error rather than as a quietly different sentence. `id` and
 * `sortOrder` are deliberately **not** read — see
 * {@link LineDescriptionInput.inclusions} on why order is the caller's to fix.
 */
export type LineDescriptionInclusion = Pick<
  LineInclusion,
  'name' | 'unitSingular' | 'unitPlural' | 'quantity'
>;

/**
 * The quantity an inclusion draft will actually be STORED with, from the raw
 * string a quantity box holds.
 *
 * # ⚠️ This exists because `Number('')` is `0`, and `0` prints the PLURAL
 *
 * An empty quantity box means *"omit the key"*, and the server reads an omitted
 * quantity as **1**. So the preview must say `1 campus`. Read naively, an empty
 * box gives `Number('') === 0`, which is neither 1 nor invalid: it fails the
 * `quantity === 1` test, selects `unitPlural`, and writes **`0 campuses`** —
 * a promise of nothing, in a preview of a customer's document, for a line that
 * will be stored as one campus. Nothing throws and the row saves fine.
 *
 * ⚠️ **It mirrors `inclusionsPayload` in `QuoteLineItemsTable`**, which decides
 * what is actually sent: it emits `quantity` only when the box is non-empty
 * **and** {@link isValidInclusionQuantity} accepts it, and omits it otherwise so
 * the server's default of 1 applies. This function answers the same question
 * from the other end — *given that, what number will the line carry?* The two
 * are one rule in two places today; the payload builder should be refactored
 * onto this one so they cannot drift.
 */
export function effectiveInclusionQuantity(raw: string): number {
  const parsed = Number(raw);
  return raw !== '' && isValidInclusionQuantity(parsed) ? parsed : 1;
}

/**
 * The four facts about a line that decide its derived description.
 *
 * Named rather than positional because `productName`, `projectName` and
 * `productCode` are all `string` and all describe the same line — see the note on
 * {@link composeLineDescription}. This is an argument-naming device and **not**
 * an extension point: nothing that selects between two possible outputs may be
 * added here.
 *
 * ⚠️ Every field here is a **fact about the line**. `productCode` is the newest
 * and is the one most likely to be mistaken for a setting — it is not. It is read
 * only to answer "does this line carry a Xero item code", never to choose a
 * variant, and a caller cannot use it to pick between two strings: on a line with
 * no project it changes nothing at all.
 */
export interface LineDescriptionInput {
  /** The catalogue product the line sells. Passed through unguarded, even when empty. */
  productName: string;
  /**
   * The project the line names, if any. `null` / `undefined` / `''` /
   * whitespace-only all mean "no project" and fall back to `productName`.
   *
   * ⚠️ **Reaches the output now** that {@link LINE_DESCRIPTION_PREFERS_PROJECT_NAME}
   * is `true`: on a line that also carries an item code, this is the head of the
   * sentence — **trimmed**, matching the backend. Omitting it on a line that has
   * one no longer degrades quietly to old wording; it disagrees with the document
   * the customer receives.
   */
  projectName?: string | null;
  /**
   * The product's **Xero item code**, if it has one. This is the guard on the
   * project-name substitution: the head becomes the project name only when this
   * is present, per {@link hasXeroItemCode} — `null` / `undefined` / `''` /
   * whitespace-only all mean "no code" and keep the product name.
   *
   * ⚠️ **Optional, and an omitted code reads as ABSENT.** A caller that forgets
   * it gets no error — it gets the product name on a line the backend heads with
   * the project name. Since the flip that is a **divergence, not a graceful
   * degradation**, and it is invisible: the string is well-formed and simply
   * names the wrong thing. It is optional only because widening it to required is
   * a breaking signature change. **Treat it as required** — see
   * {@link LINE_DESCRIPTION_PREFERS_PROJECT_NAME}.
   */
  productCode?: string | null;
  /** Every city the line runs in, in the order given. Never sorted or deduplicated here. */
  cityNames: readonly string[];
  /**
   * Every REGION the line names, in the order given — the geography of a line
   * that has **no cities to state instead**.
   *
   * # ⚠️ CITIES ELSE REGIONS. IT IS AN *EITHER*, NEVER A BOTH
   *
   * Read `geographySegment` before touching this. A line carrying both prints
   * **only its cities**: the cities are the more specific fact, and appending
   * both emits `Campus Outreach — Bangalore — India`, a sentence with two em
   * dashes that no backend composes and that destroys the split the separator
   * is exported for.
   *
   * ⚠️ **This is what the products with no project print**, and it is the whole
   * reason the member exists. EMBA, LAE and Virtual Masterclass are sold
   * without a project, so they have no cities at all and stated **no geography
   * whatsoever** until the backend added regions to both composers
   * (`6d17eea`) — after which the operator read `Virtual Masterclasses` and the
   * customer received `Virtual Masterclasses — India`. That divergence was live
   * in production, on exactly the products this member serves, and nothing
   * anywhere reported it.
   *
   * ⚠️ **Optional, and an omitted set reads as NONE** — the same silent-caller
   * hazard as `productCode` and `inclusions`, and with the same consequence
   * rather than a graceful one: a projectless line whose regions are not passed
   * shows a bare product name here and a geography on the customer's document.
   * **Treat it as required.**
   *
   * `readonly` so `.sort()` — which mutates in place — does not typecheck.
   * Nothing is sorted, deduplicated, trimmed, filtered or case-normalised here,
   * for every reason the city list gives.
   */
  regionNames?: readonly string[];
  /**
   * Every campaign inclusion the line carries, **in the order given**.
   *
   * ⚠️ **ARRIVAL ORDER, never re-sorted here — and that is a decision, not an
   * omission.** A read entry carries a `sortOrder` and the temptation is to sort
   * on it defensively. It is refused for the same reason `cityNames` is:
   *
   * - The API already returns the set in the line's own `sortOrder`, and on this
   *   side the draft array's INDEX *is* the `sortOrder` that will be written.
   *   Both are already correct; a sort here would be a third opinion.
   * - Sorting is the quietest divergence available. If this side sorts and the
   *   backend emits its loader's `ORDER BY`, the two strings contain the same
   *   words, the same punctuation and the same number of characters, differing
   *   only in sequence — which reads as a rendering quirk rather than a fault,
   *   and is therefore the one nobody reports.
   *
   * **So the ordering is pinned in the LOADERS, not in the composers**, and it
   * is a join key like the separator. That is an explicit item on the flip
   * condition — see {@link LINE_DESCRIPTION_INCLUDES_INCLUSIONS}.
   *
   * ⚠️ Optional, and an omitted set reads as NONE — the same silent-caller
   * hazard as `productCode`. While
   * {@link LINE_DESCRIPTION_INCLUDES_INCLUSIONS} WAS `false` that was a genuine
   * no-op; the moment it is `true` a caller that forgets this shows an operator
   * a line with no inclusions on it and lets the customer's document say
   * otherwise. **Treat it as required.**
   *
   * `readonly` so `.sort()` — which mutates in place — does not typecheck, and
   * the caller's own array cannot be reordered underneath it either.
   */
  inclusions?: readonly LineDescriptionInclusion[];
}

/**
 * The description a line derives from its product — or, once
 * {@link LINE_DESCRIPTION_PREFERS_PROJECT_NAME} is on, its project — and its
 * cities.
 *
 * ```
 * composeLineDescription({ productName: 'Campus Outreach', cityNames: [] })
 *   -> 'Campus Outreach'
 * composeLineDescription({ productName: 'Campus Outreach', cityNames: ['Bangalore'] })
 *   -> 'Campus Outreach — Bangalore'
 * composeLineDescription({
 *   productName: 'Campus Outreach',
 *   cityNames: ['Bangalore', 'Mumbai', 'Delhi'],
 * })
 *   -> 'Campus Outreach — Bangalore, Mumbai, Delhi'
 *
 * // LINE_DESCRIPTION_PREFERS_PROJECT_NAME is true — the project name heads the
 * // sentence, because the line carries an item code and the product's identity
 * // survives on it:
 * composeLineDescription({
 *   productName: 'Sales - Business School Festivals',
 *   projectName: 'BSF India - Summer 2027',
 *   productCode: 'BSF-IN',
 *   cityNames: ['Mumbai'],
 * })
 *   -> 'BSF India - Summer 2027 — Mumbai'
 *
 * // and the project name is emitted TRIMMED, matching the backend:
 * composeLineDescription({
 *   productName: 'Sales - Business School Festivals',
 *   projectName: '  BSF India - Summer 2027  ',
 *   productCode: 'BSF-IN',
 *   cityNames: ['Mumbai'],
 * })
 *   -> 'BSF India - Summer 2027 — Mumbai'
 *
 * // the case the guard exists for — no item code, so the PRODUCT name
 * // stays even though the line names a project:
 * composeLineDescription({
 *   productName: 'Miscellaneous',
 *   projectName: 'BSF India - Summer 2027',
 *   productCode: null,
 *   cityNames: ['Mumbai'],
 * })
 *   -> 'Miscellaneous — Mumbai'
 * ```
 *
 * ## The arguments are NAMED on purpose
 *
 * `productName`, `projectName` and `productCode` are all plain `string`s
 * describing the same line, and on a positional signature nothing — not `tsc`,
 * not oxlint, not review — can tell `(product, project, cities)` from
 * `(project, product, cities)`. Swapping them compiles, runs, and produces a
 * perfectly well-formed sentence that is wrong. An options object makes the
 * mistake unspellable: the key names it. That is the whole reason for the shape;
 * it is **not** an options object in the sense the section at the bottom of this
 * comment warns about, and it must not grow into one. Note the third string made
 * this argument stronger, not weaker — a positional `(product, project, code,
 * cities)` has two adjacent interchangeable pairs.
 *
 * ⚠️ **EVERY city, joined, IN THE ORDER GIVEN.** One EMERGE line is one Xero
 * line however many cities it names, and the order handed in is the line's
 * stored `sortOrder` — which is what Xero prints. Sorting or deduplicating here
 * would be a second divergence, and a quieter one than the separator: the words
 * would all be present and only their order would differ, which reads as a
 * rendering quirk rather than as a fault. `cityNames` is `readonly` so that
 * `.sort()` — which mutates in place — does not typecheck, and the caller's
 * array cannot be reordered underneath it either.
 *
 * ## The project name REPLACES the product name — it does not join it
 *
 * The owner said *instead of*, so exactly one of the two names appears. Both
 * names on one line was not what was asked for and is not offered.
 *
 * **And it replaces it only on a line carrying a Xero item code** — the guard on
 * {@link hasXeroItemCode}. "Instead of" is only safe while the product's identity
 * is still on the document *somewhere*, and the item code is that somewhere.
 *
 * ⚠️ **This is NOT the `a ?? b` silent-drop trap** CLAUDE.md catalogues under
 * "`a ?? b` between two things that BOTH need showing is a silent drop". That
 * rule's test is *"if both sides can be true at once, it is a bug"* — and it is
 * aimed at two **messages** or two **counts**, where the fallback is not really
 * a fallback but a second still-true fact being thrown away silently. Here the
 * substitution is the requirement: the head of the sentence is one name, and
 * which name it is was a decision, taken deliberately, recorded here. A later
 * reader "fixing" this into showing both would be reversing a decision, not
 * repairing a bug — the same distinction `shouldShowInclusionsEditor` carries.
 *
 * ## ⚠️ A flagged consequence of that decision — recorded, NOT resolved
 *
 * Now that the constant is on, **the customer-facing line no longer names the
 * product in its DESCRIPTION.** The document says `BSF India - Summer 2027 —
 * Mumbai`, and what was sold is legible only from the line's `ItemCode` column.
 * That is the trade the guard makes explicit: the product identity moves from
 * prose a customer reads to a code beside it, and it is *present either way* —
 * which is why the guard refuses the substitution on the lines where the code is
 * missing, because there the identity would not move, it would vanish.
 *
 * And because a line may lack a project **or** lack an item code, **one document
 * can carry both conventions at once** — some lines headed by a project, others
 * by a product, with nothing on the page distinguishing which kind of name a
 * reader is looking at. Today those two causes coincide (`Meet Up`,
 * `Profile Evaluations` and `Miscellaneous` have neither), so the mixing is
 * predictable; once a codeless product gains a project it will not be.
 *
 * Both are consequences of a decision the owner took, not defects, and neither
 * is decided here. They are written down so that whoever meets them next knows
 * they were foreseen and can raise them with the owner rather than silently
 * reverting the rule.
 *
 * ## The edge cases, and what was chosen
 *
 * **A blank `projectName` falls back to the product name.** `null`, `undefined`,
 * `''` and whitespace-only all count as absent — see `usableProjectName`. The
 * point is that a blank project name must never reach the output: it would give
 * `' — Mumbai'`, a leading separator over an empty head, which is the one shape
 * this function must not emit. Note this fallback is the *opposite* call from
 * the `productName` one immediately below, and deliberately so: an empty product
 * name has nothing to fall back to, while an empty project name does.
 *
 * **A surviving `projectName` is emitted TRIMMED**, because the backend's
 * deployed composer emits `projectName.trim()`. ⚠️ **That trim is on the project
 * name ONLY** — `productName` is passed through exactly as given, on both sides.
 * The two are deliberately inconsistent; see `usableProjectName`.
 *
 * **A blank `productCode` closes the guard**, by the same test and for a related
 * reason — see {@link hasXeroItemCode}. Note the two blankness tests are spelled
 * identically on purpose: one rule, asked twice, so neither can be "tidied" into
 * a `!= null` without the other looking wrong beside it.
 *
 * **No cities → the head alone**, with no trailing separator. This is
 * the backend's `cityNames.length > 0` branch, and it is the common case: most
 * lines name no city at all.
 *
 * **An empty `productName` is passed through, NOT guarded.** It yields
 * `' — Bangalore'` — a leading separator, and visibly wrong. That is
 * deliberate, and it is the one place this module declines to be helpful:
 * returning `''` or `null` instead would be *this* function deciding something
 * the backend's does not, which is the precise thing it exists not to do. The
 * backend has no such guard because its input type makes the case unreachable.
 *
 * **The guard belongs at the call site, because only the call site can tell the
 * two situations apart.** "No product chosen yet" is a half-filled row with
 * nothing to describe — a caller should render nothing rather than call this at
 * all (`descriptionOf` in `QuoteLineItemsTable` returns `null` for exactly
 * that). "A product whose name is genuinely empty" is a data fault, and it
 * should look like one rather than be quietly swallowed here.
 *
 * ## One function, one output PER CALL SITE — still on purpose
 *
 * ⚠️ **An options object exists on this function, and that does not violate the
 * rule.** The rule was never about argument shape, it was about **a call site
 * being able to choose between two strings**. That is still impossible.
 * Every caller passes the same three facts about a line and gets the one answer
 * the whole app agrees on; which head that answer uses is decided **once, in
 * this file, by a source constant**, and no argument, prop, flag or environment
 * can move it. If a call site could pick, the join would stop being pinned — a
 * caller picks the wrong variant, the preview and the Xero document disagree
 * again, and the reason now lives in a flag someone passed three files away.
 *
 * So: do not add a `variant`, a `mode`, a `preferProject` argument or a display
 * override. If a surface needs different text, that text is not this string and
 * does not belong in this file.
 *
 * PURE — no React, no hooks, no fetching. It is the whole contract, and it can
 * be reasoned about and pinned without a browser or a live Xero tenant.
 */
export function composeLineDescription({
  productName,
  projectName,
  productCode,
  cityNames,
  regionNames,
  inclusions,
}: LineDescriptionInput): string {
  // The head of the sentence: the project name if we are preferring it AND the
  // line has a usable one AND it carries a Xero item code, the product name
  // otherwise. LINE_DESCRIPTION_PREFERS_PROJECT_NAME is true, so the substitution
  // is live and byte-pinned against the backend's deployed composer — the branch
  // rule, the item-code predicate and the project-name trim all have to match.
  const head = LINE_DESCRIPTION_PREFERS_PROJECT_NAME
    ? (substitutedHead({ projectName, productCode }) ?? productName)
    : productName;

  // The geography half is IDENTICAL in every branch, and must stay that way.
  // Which name heads the sentence changes nothing about where the line runs.
  const withGeography = head + geographySegment(cityNames, regionNames);

  /*
   * The inclusions segment is APPENDED LAST and is independent of everything
   * above it — of which name heads the sentence, and of whether the line states
   * any geography. `withGeography` is deliberately whatever the previous two
   * paragraphs produced, so this branch cannot change a single byte of an
   * existing case: with no inclusions it returns that value untouched.
   *
   * ⚠️ THIS IS LIVE CODE, on QUOTES AND INVOICES ALIKE. Read the constant's
   * comment before touching it — in particular, the sentence that used to sit
   * here saying the backend "carries no inclusions at all" was true when written
   * and is not now.
   */
  return LINE_DESCRIPTION_INCLUDES_INCLUSIONS
    ? withGeography + inclusionsSegment(inclusions)
    : withGeography;
}

/**
 * ` — Bangalore, Mumbai` or ` — India, Africa`, or the EMPTY STRING when the
 * line states no geography at all.
 *
 * ```
 * (['Bangalore', 'Mumbai'], ['India'])  -> ' — Bangalore, Mumbai'   // cities win
 * ([],                      ['India'])  -> ' — India'
 * (['Mumbai'],              [])         -> ' — Mumbai'
 * ([],                      [])         -> ''
 * ([],                      undefined)  -> ''
 * ```
 *
 * # ⚠️ CITIES WIN. REGIONS ARE WHAT A LINE PRINTS WHEN IT HAS NO CITIES
 *
 * Owner's decision, mirrored from the backend's `geographySegment`, which this
 * function reproduces byte for byte. A project line's cities are the more
 * specific fact — `Campus Outreach — Bangalore, Mumbai` says where the work
 * happens and `Campus Outreach — India` says less about the same line. The
 * regions exist for the products sold with **no project** and therefore no
 * cities at all (EMBA, LAE, Virtual Masterclass), which stated no geography
 * whatsoever until both composers gained them.
 *
 * ⚠️ **ONE SEGMENT AND ONE EM DASH, NEVER TWO.** The natural "append the
 * cities, then append the regions" rewrite emits
 * `Campus Outreach — Bangalore — India` on any line that has both. That is a
 * sentence no backend composes, so it is an immediate silent divergence from
 * the textarea a person edits — **and it also breaks the split that
 * {@link LINE_DESCRIPTION_SEPARATOR} is exported for**, because given `A — B —
 * C` nothing can say which segment is which. Cities-else-regions is an EITHER.
 *
 * ⚠️ **The ORDER OF THE TWO TESTS *IS* THE RULE.** Read the regions first and
 * every project-backed line silently stops naming its cities — same words, same
 * punctuation, a less specific document, and nothing failing anywhere.
 *
 * ⚠️ **The empty-string early return is the whole of the no-dangling-separator
 * rule**, and it is a return rather than a ternary at the call site so there is
 * exactly one place that can get it wrong. The natural "compose both halves and
 * drop the empty one" rewrite leaves a trailing ` — ` on every line that states
 * no geography, **which is most of them**.
 *
 * ⚠️ **`undefined` is NONE, not "unknown".** The member is optional, so a
 * caller that forgets it lands here — see {@link LineDescriptionInput.regionNames}
 * for why that is a divergence rather than a degradation.
 */
function geographySegment(
  cityNames: readonly string[],
  regionNames: readonly string[] | undefined,
): string {
  // Cities first. Reversing these two reads is the one edit that changes every
  // project-backed line's geography without changing a character of the output
  // shape — see this function's doc.
  const names = cityNames.length > 0 ? cityNames : (regionNames ?? []);
  if (names.length === 0) return '';
  return LINE_DESCRIPTION_SEPARATOR + names.join(LINE_DESCRIPTION_CITY_JOINER);
}

/**
 * The bracketed campaign-inclusions segment, or `''` when the line carries none.
 *
 * ```
 * ' (Campus Outreach: 2 campuses; Network Partner: 1 partner)'
 * ' (Priority Support)'                 // the master records no unit
 * ''                                    // no inclusions
 * ```
 *
 * ⚠️ **No inclusions means the EMPTY STRING, never an empty pair of brackets.**
 * `Virtual Masterclasses ()` on a customer's invoice is the same class of fault
 * as the dangling separator the no-cities branch exists to avoid, and it is what
 * the natural "always bracket the joined list" rewrite produces. Most lines
 * carry no inclusion at all, so this is the common branch rather than an edge.
 *
 * ⚠️ **The per-inclusion wording is {@link renderInclusion}'s and is NOT
 * restated here.** It is the one copy of the rule that `unitSingular` prints at
 * exactly 1, `unitPlural` at everything else, and that a `null` unit drops the
 * NUMBER as well as the word — `Priority Support`, never `1 Priority Support`.
 * Note that last clause is per-QUANTITY, not per-inclusion: a master carrying
 * `unitSingular: 'campus'` and `unitPlural: null` prints `Campus Outreach: 1
 * campus` at one and the bare `Campus Outreach` at two, and one carrying only
 * `unitPlural` prints the bare name at ONE. That is the contract's rule, it is
 * genuinely surprising, and it has two fixture rows — `I9` and `I13` — because
 * one of them alone still passes an implementation that falls back from the
 * missing unit to the present one, which is the version that prints
 * `1 campuses`.
 *
 * Nothing is pluralised, deduplicated, sorted, trimmed or case-normalised here,
 * for the reasons the whole file gives.
 */
function inclusionsSegment(inclusions: readonly LineDescriptionInclusion[] | undefined): string {
  if (inclusions === undefined || inclusions.length === 0) return '';
  const written = inclusions.map((entry) => renderInclusion(entry, entry.quantity));
  return (
    LINE_DESCRIPTION_INCLUSIONS_OPEN +
    written.join(LINE_DESCRIPTION_INCLUSION_JOINER) +
    LINE_DESCRIPTION_INCLUSIONS_CLOSE
  );
}

/**
 * The name that should REPLACE the product name at the head of the sentence, or
 * `null` to keep the product name.
 *
 * Both conditions of the owner's guarded rule, in one place and in this order:
 *
 * 1. the line must carry a **Xero item code** ({@link hasXeroItemCode}), and
 * 2. the project name must be **usable** (`usableProjectName`).
 *
 * The order is not load-bearing — both must hold and neither can throw — but the
 * code check reads first because it is the *guard*, and the project name is what
 * it guards. A reader who meets `usableProjectName` first tends to read the code
 * check as an afterthought, which is how a guard gets deleted.
 *
 * ⚠️ **It returns `null`, not `productName`.** The fallback is applied once, by
 * the caller. Returning the product name from here would put the fallback in two
 * places, and the two would be one edit apart from disagreeing about which name a
 * blank-project-with-a-code line gets.
 *
 * Deliberately takes only the two fields it judges: it cannot see `productName`,
 * so it cannot quietly become a second composer.
 */
function substitutedHead({
  projectName,
  productCode,
}: Pick<LineDescriptionInput, 'projectName' | 'productCode'>): string | null {
  if (!hasXeroItemCode(productCode)) return null;
  return usableProjectName(projectName);
}

/**
 * The line's project name if it is one we can actually put in front of a
 * customer, `null` otherwise.
 *
 * Absent means `null`, `undefined`, `''` **or whitespace-only** — a line can
 * carry any of the four and all four must fall back to the product name rather
 * than emit an empty head.
 *
 * # ⚠️ PINNED: tested trimmed, **emitted trimmed**
 *
 * The backend's deployed composer reads the project name as
 * `line.project?.name.trim() ?? ''` and emits
 * `` `${projectName.trim()} — ${cities}` ``. So the trimmed value is what
 * lands on the customer's Xero document, and this function must return the
 * same. Do not remove the trim: on `'  BSF India  '` an untrimmed emit gives
 * `'  BSF India   — Mumbai'` against the backend's `'BSF India — Mumbai'` —
 * same branch, different string, and nothing compares them at runtime.
 *
 * ⚠️ **This trims the PROJECT NAME ONLY.** The `productName` fallback is a
 * different call and is deliberately passed through unguarded and untrimmed — see
 * {@link composeLineDescription}, which states why. Do not "make it consistent":
 * the backend does not trim the product name either, and adding a trim here would
 * be the divergence this whole file exists to prevent, arriving from the tidy
 * direction.
 */
function usableProjectName(projectName: string | null | undefined): string | null {
  if (projectName === null || projectName === undefined) return null;
  const trimmed = projectName.trim();
  return trimmed === '' ? null : trimmed;
}

/* ========================================================================== *
 * THE SAMENESS FIXTURES — the agreed answer, shared with the backend
 * ========================================================================== *
 *
 * ⚠️ **This repo has no test runner.** `package.json` has `dev`, `build`, `lint`
 * and `preview` and nothing else — no vitest, no jest. This block is therefore
 * the sameness test *written down but not run in CI*, so that whoever adds a
 * runner finds it pre-specified rather than re-deriving it. **Do not add a
 * framework to this repo just to run it**; when one arrives, transcribe the table
 * below and delete this paragraph.
 *
 * Every row has been executed against this module directly — the real modules
 * loaded through vite's SSR loader, every row asserted against
 * `composeLineDescription` (and, for the `I` rows, against a flag-true
 * reconstruction built from the exported constants and the real
 * `renderInclusion`) — along with the full 5 × 5 × 3 cartesian product of the
 * same input values, plus **46 deliberately WRONG implementations** run
 * against every row: 21 description rows, 16 inclusion rows, 5 quantity rows,
 * ⚠️ **(that inclusion-row count is the 2026-08-30 reading and the table now
 * holds 21 — see the re-run recorded under the inclusion rows below)**,
 * 1,387 assertions. That proves this composer matches the table, not that the
 * backend's does. Only the backend asserting the same rows closes that, which
 * is what the table is for. The wrong implementations are the point, and the
 * method is written up below.
 *
 * ## Why a SAMENESS test, and not a test of our string
 *
 * The backend hit this exact failure and fixed it the right way: their quote and
 * invoice tax resolvers had silently diverged while **both suites stayed green
 * the whole time**, because *a disagreement between two files is not a property
 * of either file*. Their fix was `tests/xero-tax-source-mirror.test.ts` — a test
 * that asserts the two agree. Their framing, which is worth keeping verbatim
 * because it is the whole argument:
 *
 * > A test that pins my string and a test that pins yours can both be "fixed"
 * > into a divergence; a test that pins sameness cannot.
 *
 * So the point of the table below is **not** that our composer returns these
 * strings. It is that OURS and THEIRS return the *same* string for the same line.
 * Ours passing on its own proves nothing — that is precisely the state the
 * backend's two tax resolvers were in.
 *
 * ## How to use it
 *
 * There is **one** expected column, and it is the live agreed answer.
 *
 * Send this table to the emerge-backend-app peer and have them assert their
 * composer against the same pairs. That is the point: it states the agreed answer
 * for every case rather than describing the rule in prose twice — prose is how the
 * two blankness tests and the trim would drift — and two independent pins become
 * one shared one.
 *
 * Inputs are written **literally**, including quotes, so whitespace is visible.
 * **Where an EXPECTED value carries significant leading, trailing or doubled
 * whitespace it is quoted too**, for the same reason — an invisible trailing
 * space is exactly the divergence these rows exist to catch, and it cannot be
 * proof-read out of a bare backtick.
 *
 * `productName` is `'Campus Outreach'` on every row **except 18**, which is the
 * one row that pins it and states its own value.
 *
 * | #  | projectName                     | productCode  | cityNames                          | EXPECTED (both sides)                                |
 * |----|---------------------------------|--------------|------------------------------------|------------------------------------------------------|
 * |  1 | null                            | null         | []                                 | `Campus Outreach`                                    |
 * |  2 | null                            | null         | ['Mumbai']                         | `Campus Outreach — Mumbai`                           |
 * |  3 | null                            | null         | ['Bangalore', 'Mumbai', 'Delhi']   | `Campus Outreach — Bangalore, Mumbai, Delhi`         |
 * |  4 | null                            | 'BSF-IN'     | ['Bangalore', 'Mumbai', 'Delhi']   | `Campus Outreach — Bangalore, Mumbai, Delhi`         |
 * |  5 | 'BSF India - Summer 2027'       | null         | ['Mumbai']                         | `Campus Outreach — Mumbai`                           |
 * |  6 | 'BSF India - Summer 2027'       | ''           | ['Mumbai']                         | `Campus Outreach — Mumbai`                           |
 * |  7 | 'BSF India - Summer 2027'       | '   '        | ['Mumbai']                         | `Campus Outreach — Mumbai`                           |
 * |  8 | 'BSF India - Summer 2027'       | 'BSF-IN'     | []                                 | `BSF India - Summer 2027`                            |
 * |  9 | 'BSF India - Summer 2027'       | 'BSF-IN'     | ['Mumbai']                         | `BSF India - Summer 2027 — Mumbai`                   |
 * | 10 | 'BSF India - Summer 2027'       | 'BSF-IN'     | ['Bangalore', 'Mumbai', 'Delhi']   | `BSF India - Summer 2027 — Bangalore, Mumbai, Delhi` |
 * | 11 | ''                              | 'BSF-IN'     | ['Mumbai']                         | `Campus Outreach — Mumbai`                           |
 * | 12 | '   '                           | 'BSF-IN'     | ['Mumbai']                         | `Campus Outreach — Mumbai`                           |
 * | 13 | '  BSF India - Summer 2027  '   | 'BSF-IN'     | ['Mumbai']                         | `BSF India - Summer 2027 — Mumbai`                   |
 * | 14 | 'BSF India - Summer 2027'       | '  BSF-IN  ' | ['Mumbai']                         | `BSF India - Summer 2027 — Mumbai`                   |
 * | 15 | (key omitted)                   | (key omitted)| ['Mumbai']                         | `Campus Outreach — Mumbai`                           |
 * | 16 | null                            | null         | ['  Mumbai  ']                     | `'Campus Outreach —   Mumbai  '`                     |
 * | 17 | null                            | null         | ['Mumbai', 'Mumbai']               | `Campus Outreach — Mumbai, Mumbai`                   |
 * | 18 | null                            | null         | ['Mumbai']                         | `'  Campus Outreach   — Mumbai'`                     |
 * | 19 | null                            | null         | ['Mumbai', '']                     | `'Campus Outreach — Mumbai, '`                       |
 * | 20 | null                            | null         | ['Ras al-Khaimah']                 | `Campus Outreach — Ras al-Khaimah`                   |
 * | 21 | '\u00a0BSF India - Summer 2027\u00a0' | 'BSF-IN' | ['Mumbai']  | `BSF India - Summer 2027 — Mumbai`                   |
 *
 * Two rows carry an input that cannot be read off the columns above:
 *
 * - **18** is the only row whose `productName` is not `'Campus Outreach'`. It is
 *   `'  Campus Outreach  '`, two spaces either side, and it is emitted **exactly
 *   as given** — the product name is never trimmed, on either side.
 * - **21**'s `projectName` is spelled with **U+00A0 NO-BREAK SPACE**
 *   escapes, `\u00a0`, for exactly the reason the separator is spelled
 *   `\u2014`: a NBSP is indistinguishable from a space in every editor font,
 *   so a literal one would be a fixture whose entire point is invisible. A reader
 *   re-typing the row would write an ordinary space, reproduce row 13 and pin
 *   nothing; a reader copying it could not tell which character they had. (A
 *   literal NBSP is also an oxlint `no-irregular-whitespace` warning — the
 *   lesser of the two reasons, but they point the same way.)
 *
 *   ⚠️ **The escape and the character are the same bytes**, and that was
 *   checked rather than assumed: this table is parsed back out of the source and
 *   compared, cell by cell, against what the composer actually returns.
 *
 *   A NBSP is what arrives when a name is pasted from a web page, Word or Excel.
 *   `String.prototype.trim()` strips it — verified by execution — while a
 *   hand-rolled `/^ +| +$/` does not, which is the whole divergence this row
 *   exists to catch.
 *
 * The em dash in every expected value above is U+2014 with a space either side —
 * see {@link LINE_DESCRIPTION_SEPARATOR}, and the bytes are `20 e2 80 94 20`. If a
 * reader is unsure which dash they are looking at, that is the point of writing
 * the separator as an escape, and it is why these are fixtures rather than prose.
 *
 * ## The rows that are actually load-bearing
 *
 * Most of the table is regression ballast. The bullets below carry the
 * disagreements that would otherwise be invisible, and a mirror test that drops
 * them is not a mirror test.
 *
 * - **5** — the GUARD itself. A project, no code, and the PRODUCT name stays.
 *   This is the row that fails if either side ships the unguarded rule.
 * - **6 and 7** — `''` and `'   '` count as NO code. 7 is the one that separates
 *   the three plausible implementations: `!= null` fails 6 and 7, a truthiness
 *   test (`if (productCode)`) passes 6 and **fails 7**, and only
 *   non-empty-after-trim passes both. If only one row from this table survives,
 *   make it 7.
 * - **13** — the TRIM. The project name is tested trimmed **and emitted
 *   trimmed**, so there is exactly one space before the dash. A side that
 *   emits it raw produces `'  BSF India - Summer 2027   — Mumbai'` — same
 *   branch, same words, different bytes, and nothing compares them.
 * - **12** — the trim's other half: whitespace-only is not a name, so the head
 *   falls back to the product rather than emitting an empty head. 12 and 13 have
 *   to be read together, or "trim it" and "treat blank as absent" look like one
 *   rule with one obvious implementation, and they are two.
 * - **8** — the no-cities branch under substitution: head alone, no trailing
 *   separator, with the project name as the head.
 * - **15** — the omitted-argument case, ours only. `productCode` is an optional
 *   parameter, so a call site that forgets it silently keeps the product name
 *   while the backend, reading the line's real code, leads with the project name.
 *   The backend has no equivalent — its fields are not optional — so this row
 *   pins a **frontend** hazard rather than a shared answer.
 * - **16, 17, 19 and 20** — the CITY LIST is emitted verbatim, in arrival order.
 *   Each pins one "clean up the input" reflex that rows 1–15 could not see,
 *   because every city in them is a tidy, unique, unpadded, Title-Case name:
 *   16 a padded name (nothing is trimmed), 17 a repeated one (nothing is
 *   deduplicated), 19 a blank one (nothing is filtered out), 20
 *   `'Ras al-Khaimah'` (nothing is case-normalised — upper- and lower-casing were
 *   already caught by any city row, but **title-casing is a no-op on every name
 *   in rows 1–15** and is the form the reflex actually takes).
 * - **18** — the `productName` is NOT trimmed, and this is the row that says so.
 *   The project name is trimmed and the product name is not; that asymmetry is
 *   deliberate on both sides (see `usableProjectName`), it looks like an
 *   oversight, and "make it consistent" is a one-word edit **that nothing else in
 *   this table catches** — every other row's product name is already clean.
 * - **21** — `.trim()` means JavaScript's `trim()`, which strips **U+00A0** and
 *   every other Unicode whitespace, not just `' '`. Row 13 pins the trim; row 21
 *   pins its *definition*. A hand-rolled `/^ +| +$/` — or any language's
 *   space-only trim on the other side of this join — takes the identical branch
 *   as row 13 and emits a different string.
 *
 * ## ⚠️ HOW ROWS 16–21 WERE CUT: enumerate the WRONG implementations
 *
 * **A shared fixture is only as good as its worst-covered edge, and the edges you
 * add are the ones your own implementation made you think of.** Writing rows by
 * reading the correct composer produces rows the correct composer passes, which
 * is the one thing already guaranteed. The method that works is the opposite:
 * **enumerate the plausible WRONG implementations and require the table to kill
 * each one.** A candidate no row kills is a hole, not a hypothetical.
 *
 * That method is not a style preference — it is how `I13` was found (see the
 * inclusion rows below), and `I13` is the only row in either table that catches
 * the most natural defensive rewrite of the wording rule. Nothing about the
 * correct implementation suggests `I13` exists.
 *
 * Applied to rows 1–15: 29 wrong composers were implemented and run against
 * every row. Six were killed by nothing, and rows 16–21 are exactly those six
 * holes.
 *
 * | plausible wrong implementation | killed by |
 * |---|---|
 * | `projectName !== '' \|\| itemCode !== ''` — OR instead of AND | 4, 5, 6, 7, 11, 12 |
 * | `projectName !== ''` alone — the item-code condition forgotten | 5, 6, 7 |
 * | the guard REVERSED — project name only when there is NO code | 5, 6, 7, 8, 9, 10, 13, 14, 21 |
 * | both names on the head, joined rather than substituted | 8, 9, 10, 13, 14, 21 |
 * | `projectName ?? productName` for the head | 11, 12, 13, 21 |
 * | truthiness on the RAW project name (`projectName ? …`) | 12 |
 * | truthiness on the RAW product code (`productCode ? …`) | 7 |
 * | `productCode != null` instead of trimmed-non-empty | 6, 7 |
 * | `projectName != null` instead of trimmed-non-empty | 11, 12 |
 * | project name tested trimmed, EMITTED raw | 13, 21 |
 * | project name never trimmed at all | 12, 13, 21 |
 * | project trimmed with `/^ +\| +$/` rather than `.trim()` | **21** |
 * | `productName` trimmed too, "for consistency" | **18** |
 * | the whole head trimmed | **18** |
 * | cities joined with `,` rather than `', '` | 3, 4, 10, 17, 19 |
 * | Oxford `and` before the last city | 3, 4, 10, 17, 19 |
 * | city names SORTED | 3, 4, 10, 19 |
 * | city names DEDUPLICATED | **17** |
 * | city names TRIMMED | **16** |
 * | blank city names FILTERED OUT | **19** |
 * | city names UPPERCASED or lowercased | every row with a city |
 * | city names TITLE-CASED | **20** |
 * | the separator emitted on an EMPTY city list | 1, 8 |
 * | EN DASH, HYPHEN-MINUS, an unspaced EM DASH, or NBSPs around it | every row with a city |
 * | truthiness on the TRIMMED values (`t && c ? t : productName`) | **nothing — see below** |
 *
 * ### The one candidate nothing kills, and why that is CORRECT rather than a hole
 *
 * ⚠️ **`t && c ? t : productName` is not a wrong implementation at all**, and the
 * brief that produced this exercise assumed it was — "identical until a name is
 * `'0'`". It is not: **`'0'` is truthy in JavaScript.** The only falsy string is
 * `''`, so for a `string` operand `s !== ''` and `Boolean(s)` are the same test,
 * always. Verified by execution over `''`, `'0'`, `'   '`, `'a'`, `'false'`,
 * `'NaN'`.
 *
 * **Do not add a row for it**, and this is the useful half: a fixture row pinning
 * a distinction that does not exist would be permanent dead weight in a table two
 * repositories maintain, and it would teach the next reader that truthiness is
 * the hazard here. It is not. The hazard is truthiness applied to the **raw**
 * value *before* the trim and the `?? ''` — which is a different candidate, and
 * rows 7 and 12 kill it.
 *
 * ## THE GEOGRAPHY ROWS — pinning CITIES-ELSE-REGIONS
 *
 * These pin `geographySegment`, added when the backend put `regionNames` on both
 * composers (`6d17eea`). Until then a projectless line printed no geography here
 * and a region on the customer's document — a live divergence on EMBA, LAE and
 * Virtual Masterclass, which are precisely the products with no project.
 *
 * ⚠️ **THE NUMBERING IS APPEND-ONLY AND THE PREFIX IS ITS OWN**, for the reason
 * the `I` rows give: these are cited by number across two repositories, so `R3`
 * must mean the same line for ever. A new case gets the next free `R`.
 *
 * `productName` is `'Virtual Masterclasses'` on every row; `projectName` and
 * `productCode` are `null` throughout, so the head is the product name and every
 * row isolates the geography.
 *
 * | #  | cityNames               | regionNames          | EXPECTED (both sides)                            |
 * |----|-------------------------|----------------------|--------------------------------------------------|
 * | R1 | []                      | []                   | `Virtual Masterclasses`                          |
 * | R2 | []                      | (key omitted)        | `Virtual Masterclasses`                          |
 * | R3 | []                      | ['India']            | `Virtual Masterclasses — India`                  |
 * | R4 | []                      | ['India', 'Africa']  | `Virtual Masterclasses — India, Africa`          |
 * | R5 | ['Mumbai']              | []                   | `Virtual Masterclasses — Mumbai`                 |
 * | R6 | ['Mumbai']              | (key omitted)        | `Virtual Masterclasses — Mumbai`                 |
 * | R7 | ['Mumbai']              | ['India']            | `Virtual Masterclasses — Mumbai`                 |
 * | R8 | ['Bangalore', 'Mumbai'] | ['India', 'Africa']  | `Virtual Masterclasses — Bangalore, Mumbai`      |
 * | R9 | []                      | ['  India  ']        | `'Virtual Masterclasses —   India  '`            |
 * | R10| []                      | ['India', 'India']   | `Virtual Masterclasses — India, India`           |
 * | R11| []                      | ['Middle East & North Africa'] | `Virtual Masterclasses — Middle East & North Africa` |
 *
 * `R9`'s expected value carries significant whitespace, so it is quoted.
 *
 * ### The rows that are actually load-bearing
 *
 * - 🔴 **R1 and R2 — THE REGRESSION PIN, and they come first for that reason.**
 *   A line with no cities and no regions must be **byte-identical to what it was
 *   before regions existed**, and R2 pins the *omitted key* separately from the
 *   empty array because the member is optional and `undefined.length` throws.
 *   With no inclusions either, these two are the whole "nothing changed" claim.
 * - **R5 and R6** — the other half of that pin: a line with cities and no regions
 *   is unchanged too. R6 is again the omitted key. Together with R1/R2 these four
 *   rows are every shape that existed before this change.
 * - ⚠️ **R7 and R8 — CITIES WIN, and these are the only rows that catch the
 *   append-both rewrite.** A composer that concatenates emits
 *   `Virtual Masterclasses — Mumbai — India`: two em dashes, well-formed, and it
 *   destroys the separator split. R8 makes it multi-valued on both sides so a
 *   "join whichever is longer" version fails too.
 * - **R3 and R4** — the case the member was added for: no cities, so the regions
 *   are the geography, joined `', '` exactly as cities are. A composer with a
 *   second joiner for regions fails R4 alone.
 * - **R9, R10 and R11** — the region list is emitted **verbatim, in arrival
 *   order**, one row per "clean up the input" reflex, matching what rows 16–21 do
 *   for cities. R9 padded (nothing is trimmed), R10 repeated (nothing is
 *   deduplicated), R11 `'Middle East & North Africa'` — which is **also a Xero
 *   tracking join key** and must never be shortened to `MENA`, so it pins the
 *   abbreviating reflex in the one place it would be most expensive.
 *
 * | plausible wrong implementation | killed by |
 * |---|---|
 * | cities and regions both appended | **R7, R8** |
 * | regions read FIRST, cities as the fallback | **R7, R8** |
 * | regions appended with a SECOND em dash when cities exist | **R7, R8** |
 * | regions joined with something other than `', '` | **R4** |
 * | the separator emitted on an empty geography | **R1, R2** |
 * | `regionNames.length` read without the optional guard | **R2, R6** (throws) |
 * | region names TRIMMED | **R9** |
 * | region names DEDUPLICATED | **R10** |
 * | region names SORTED | **R4** (`India, Africa` is not alphabetical) |
 * | `Middle East & North Africa` abbreviated | **R11** |
 *
 * ## THE INCLUSIONS ROWS — pinning `LINE_DESCRIPTION_INCLUDES_INCLUSIONS: true`
 *
 * These pin {@link LINE_DESCRIPTION_INCLUDES_INCLUSIONS} **`true`**, which it
 * is, on quotes. The FORMAT they assert is the owner's decision — bare
 * brackets, no `Includes:` label — so there is nothing here for the backend to
 * approve, only to assert against their own composer.
 *
 * ⚠️ **THE NUMBERING IS APPEND-ONLY.** These rows are handed to the backend
 * verbatim and cited by number in both repos' notes, so `I7` must mean the same
 * line for ever. A new case gets the next free number at the end of the table it
 * belongs to — **never** an insertion that renumbers what follows, and never a
 * reuse of a number whose row was removed. A shared fixture whose labels shift is
 * worse than no shared fixture: the two sides go on quoting `I9` at each other
 * while meaning different rows.
 *
 * Inclusions are named below rather than spelled out per row. Each is
 * `{name, unitSingular, unitPlural, quantity}`:
 *
 * ```
 * CO1     {'Campus Outreach',   'campus',  'campuses', 1}  -> Campus Outreach: 1 campus
 * CO2     {'Campus Outreach',   'campus',  'campuses', 2}  -> Campus Outreach: 2 campuses
 * NP1     {'Network Partner',   'partner', 'partners', 1}  -> Network Partner: 1 partner
 * PS1     {'Priority Support',   null,      null,      1}  -> Priority Support
 * HALF2   {'Campus Outreach',   'campus',   null,      2}  -> Campus Outreach
 * HALF1   {'Campus Outreach',    null,     'campuses', 1}  -> Campus Outreach
 * PADNAME {'  MBA fairs  ',     'campus',  'campuses', 2}  -> MBA fairs: 2 campuses
 *                                                             (the name is TRIMMED — see I14)
 * PADUNIT {'Campus Outreach',   'campus',  '  campuses  ', 2}
 *                                                          -> Campus Outreach: 2 campuses
 *                                                             (the unit is TRIMMED — see I15)
 * BIG     {'Campus Outreach',   'campus',  'campuses', 1500}
 *                                                          -> Campus Outreach: 1500 campuses
 * GAPNAME {'  MBA  fairs  ',    'campus',  'campuses', 2}  -> 'MBA  fairs: 2 campuses'
 *                                                             (ENDS only — the doubled
 *                                                              interior space survives)
 * WSNAME  {'   ',               'campus',  'campuses', 2}  -> ': 2 campuses'
 *                                                             (a name that trims to nothing
 *                                                              is still EMITTED)
 * PADPS   {'  Priority Support  ', null,    null,      1}  -> Priority Support
 *                                                             (trimmed on the NO-UNIT branch too)
 * NONAME  {'',                   null,     null,      1}  -> ''
 * NBSPNM  {'<U+00A0>MBA fairs<U+00A0>', 'campus', 'campuses', 2}
 *                                                          -> MBA fairs: 2 campuses
 *                                                             (<U+00A0> is a NO-BREAK SPACE,
 *                                                              named rather than typed — see
 *                                                              the note under I21)
 * ```
 *
 * `HALF2` and `HALF1` are the same half-configured master seen from its two
 * ends, and they are not interchangeable — see `I9` and `I13` below.
 *
 * `productName` is `'Virtual Masterclasses'` on every row. `projectName` and
 * `productCode` are `null` unless the row states otherwise.
 *
 * | #   | inclusions      | cityNames             | EXPECTED (both sides)                                                                     |
 * |-----|-----------------|-----------------------|-------------------------------------------------------------------------------------------|
 * | I1  | []              | []                    | `Virtual Masterclasses`                                                                    |
 * | I2  | []              | ['Mumbai']            | `Virtual Masterclasses — Mumbai`                                                            |
 * | I3  | (key omitted)   | ['Mumbai']            | `Virtual Masterclasses — Mumbai`                                                            |
 * | I4  | [CO1]           | []                    | `Virtual Masterclasses (Campus Outreach: 1 campus)`                                         |
 * | I5  | [CO2]           | ['Mumbai']            | `Virtual Masterclasses — Mumbai (Campus Outreach: 2 campuses)`                              |
 * | I6  | [CO2, NP1]      | ['Mumbai', 'Delhi']   | `Virtual Masterclasses — Mumbai, Delhi (Campus Outreach: 2 campuses; Network Partner: 1 partner)` |
 * | I7  | [NP1, CO2]      | []                    | `Virtual Masterclasses (Network Partner: 1 partner; Campus Outreach: 2 campuses)`           |
 * | I8  | [PS1]           | ['Mumbai']            | `Virtual Masterclasses — Mumbai (Priority Support)`                                          |
 * | I9  | [HALF2]         | []                    | `Virtual Masterclasses (Campus Outreach)`                                                   |
 * | I10 | [CO2, NP1, PS1] | []                    | `Virtual Masterclasses (Campus Outreach: 2 campuses; Network Partner: 1 partner; Priority Support)` |
 * | I13 | [HALF1]         | []                    | `Virtual Masterclasses (Campus Outreach)`                                                   |
 * | I14 | [PADNAME]       | []                    | `Virtual Masterclasses (MBA fairs: 2 campuses)`                                             |
 * | I15 | [PADUNIT]       | []                    | `Virtual Masterclasses (Campus Outreach: 2 campuses)`                                       |
 * | I16 | [BIG]           | []                    | `Virtual Masterclasses (Campus Outreach: 1500 campuses)`                                    |
 * | I17 | [GAPNAME]       | []                    | `'Virtual Masterclasses (MBA  fairs: 2 campuses)'`                                          |
 * | I18 | [WSNAME]        | []                    | `Virtual Masterclasses (: 2 campuses)`                                                      |
 * | I19 | [NONAME]        | []                    | `Virtual Masterclasses ()`                                                                  |
 * | I20 | [PADPS]         | []                    | `Virtual Masterclasses (Priority Support)`                                                  |
 * | I21 | [NBSPNM]        | []                    | `Virtual Masterclasses (MBA fairs: 2 campuses)`                                             |
 *
 * `I17` carries significant DOUBLED whitespace in its EXPECTED value, so it is
 * quoted — for the reason the first table quotes its whitespace-bearing rows: a
 * doubled interior space cannot be proof-read out of a bare backtick, and it is
 * the entire content of the row.
 *
 * `I21`'s inclusion name is padded with **U+00A0 NO-BREAK SPACE**, written above
 * as the NAMED codepoint `<U+00A0>` rather than typed, for exactly the reason row
 * **21** of the first table spells its own as an escape: a NBSP is
 * indistinguishable from a space in every editor font, so a literal one would be
 * a fixture whose entire point is invisible — a reader re-typing the row would
 * write an ordinary space, reproduce `I14`, and pin nothing. (A literal NBSP is
 * also an oxlint `no-irregular-whitespace` warning.) `I21` is the
 * inclusion-name twin of row 21, and it earns its place the same way: `.trim()`
 * strips U+00A0 while a hand-rolled `/^ +| +$/` does not, so the two take the
 * identical branch and emit different strings.
 *
 * 🔴 **`I15`'s EXPECTED VALUE WAS WRONG AND IS CORRECTED HERE — 2026-08-31.**
 * *SUPERSEDED: `'Virtual Masterclasses (Campus Outreach: 2   campuses  )'`.* It
 * stated the unit **untrimmed**, which is a string **neither** implementation
 * emits: `inclusionUnit` in `src/lib/inclusions.ts` returns `raw?.trim()` and
 * the backend's `inclusionText` does `form?.trim() ?? ''`, both by explicit
 * agreement. `inclusions.ts`'s own header says in as many words that *"`I15`
 * pins the trimmed value"* — so the module describing the rule and the table
 * pinning it disagreed, and the table was the wrong one.
 *
 * ⚠️ **A shared fixture row that no correct implementation passes is worse than
 * a missing row, and it fails in the direction that costs most**: it reports the
 * CORRECT composer as broken. Anybody who ran this table would have "fixed" a
 * trim that two repositories had agreed on, and the repair would have looked
 * like restoring agreement. The only reason it was caught is that the table was
 * executed rather than read — **prose beside a fixture is not a check on it.**
 *
 * `I13` is out of sequence because the numbering is append-only — see above. It
 * was added after `I11` and `I12` already existed in the table below, and
 * renumbering to tidy it would break the one property that makes this table
 * shareable.
 *
 * Two more rows exercise the interaction with the project-name head. Both carry
 * `projectName: 'BSF India - Summer 2027'` and `inclusions: [CO2]`:
 *
 * | #   | productCode | cityNames  | EXPECTED (both sides)                                                    |
 * |-----|-------------|------------|--------------------------------------------------------------------------|
 * | I11 | 'BSF-IN'    | ['Mumbai'] | `BSF India - Summer 2027 — Mumbai (Campus Outreach: 2 campuses)`          |
 * | I12 | null        | ['Mumbai'] | `Virtual Masterclasses — Mumbai (Campus Outreach: 2 campuses)`            |
 *
 * ### The rows that are actually load-bearing
 *
 * - **I1, I2 and I3** — the NO-CHANGE rows, and the reason they come first. A
 *   line with no inclusions must be byte-identical to what it is today, opening
 *   bracket included: `Virtual Masterclasses ()` is the same fault as a dangling
 *   separator, and it is what "always bracket the joined list" produces. I3 adds
 *   the omitted-key case, which is the frontend's own hazard — the field is
 *   optional, so a call site that forgets it reads as "no inclusions".
 * - **I4** — inclusions with **no cities**. There is no em dash anywhere in this
 *   string. A composer that reuses the city separator to introduce the segment
 *   emits `Virtual Masterclasses — Campus Outreach: 1 campus`, which is
 *   byte-identical to a line running in a city of that name. If only one row of
 *   this table survives, make it I4.
 * - **I6** — cities AND inclusions together, and the row where the two joiners
 *   sit four characters apart: `, ` between the cities and `; ` between the
 *   inclusions. Written both as `, `, `Mumbai, Delhi, Campus Outreach: 2
 *   campuses…` is one flat list of unrelated things.
 * - **I7** — ARRIVAL ORDER. `NP1` before `CO2`, deliberately neither alphabetical
 *   nor the master's `sortOrder`. This is the row that fails if either side sorts
 *   defensively, and it is the divergence nobody would report: same words, same
 *   punctuation, same length, different sequence. The two LOADERS have to agree,
 *   not the two composers.
 * - **I8, I9 and I13** — the null-unit rule, and all three must be read together.
 *   I8 is the simple case, a master with no units at all printing a bare name and
 *   **no number**. I9 and I13 are the ones that surprise people, and they are the
 *   same half-configured master from its two ends:
 *
 *   | row | unitSingular | unitPlural | quantity | prints |
 *   |-----|--------------|------------|----------|--------|
 *   | I8  | `null`       | `null`     | 1        | `Priority Support` |
 *   | I9  | `'campus'`   | `null`     | **2**    | `Campus Outreach` |
 *   | I13 | `null`       | `'campuses'` | **1**  | `Campus Outreach` |
 *
 *   **The unit is chosen by the quantity FIRST, and only then tested for
 *   `null`.** So the same master prints `Campus Outreach: 1 campus` at one and
 *   the bare `Campus Outreach` at two (I9), or the bare name at one and
 *   `Campus Outreach: 2 campuses` at two (I13). It is per-quantity, never
 *   per-inclusion.
 *
 *   Three plausible implementations were run against these rows, and the
 *   results are why **I13 had to be added** — I8 and I9 alone let one of them
 *   through:
 *
 *   | implementation | I8 | I9 | I13 |
 *   |---|---|---|---|
 *   | `quantity === 1 ? singular : plural`, then test the RESULT for null — **correct** | pass | pass | pass |
 *   | `singular === null && plural === null ? name : …` — the "is this master unitless?" branch | pass | **fail** `…: 2 null` | **fail** `…: 1 null` |
 *   | `(quantity === 1 ? singular : plural) ?? singular ?? plural` — symmetric coalesce | pass | **fail** `…: 2 campus` | **fail** `…: 1 campuses` |
 *   | `quantity === 1 && singular !== null ? singular : plural` — "prefer the singular when we have one" | pass | pass | **fail** `…: 1 campuses` |
 *
 *   - The **unitless-branch** version is at least loud: it emits the literal
 *     `null` into the sentence, so it is caught the first time anyone looks.
 *   - The **coalescing** versions are the dangerous ones, and they are the `a ?? b`
 *     silent-drop trap CLAUDE.md catalogues, arriving where nobody looks for it.
 *     Both produce **`Campus Outreach: 1 campuses`** — the exact string
 *     `src/lib/inclusions.ts` exists to make impossible, on a document a customer
 *     is invoiced against, and perfectly well-formed to a build, a linter and a
 *     reviewer.
 *   - ⚠️ **The last row is the whole reason I13 exists.** *"Prefer the singular
 *     when we have one"* is the most natural way to write this defensively, it
 *     passes I8 **and I9**, and **I13 is the only row in either table that catches
 *     it.** A shared fixture that a wrong implementation passes is worse than no
 *     fixture, because both sides then read it as independent confirmation.
 * - **I11 and I12** — the segment is independent of the head. I12 is the
 *   item-code guard still doing its job with inclusions present: no code, so the
 *   PRODUCT name heads the sentence, and the inclusions are appended either way.
 *
 * - **I14, I15, I17 and I21** — `name`, `unitSingular` and `unitPlural` are all
 *   printed **trimmed at the ENDS and otherwise verbatim**. I14's name is padded
 *   *and* carries an internal capitalisation a title-caser would destroy
 *   (`'  MBA fairs  '` → `Mba Fairs`). Every other row in this table uses tidy
 *   Title-Case names, so a composer that "cleans" what it was told passes all of
 *   I1–I13. Upper- and lower-casing were already caught by I4; **title-casing is
 *   a no-op on every name in I1–I13**, and it is the form the reflex actually
 *   takes.
 *
 *   🔴 **I14'S EXPECTED VALUE CHANGED ON 2026-08-31, AND THE FRONTEND MOVED TO
 *   MEET IT.** *SUPERSEDED: `'Virtual Masterclasses (  MBA fairs  : 2
 *   campuses)'`, carrying a note that recorded this as "THE ONE ROW THE TWO
 *   SIDES DO NOT AGREE ON TODAY … RAISED, DELIBERATELY NOT 'FIXED' HERE".* The
 *   disagreement was real: the backend's `inclusionText` opens
 *   `const name = inclusion.name.trim();` and this side emitted the raw name, so
 *   a padded name printed one way in the preview and another on the customer's
 *   document — same branch, different bytes, nothing comparing them.
 *
 *   ⚠️ **It was settled by asking which string a CUSTOMER receives, not which
 *   file was written first.** The pushed document is authoritative, so the
 *   preview aligned to it: trimming here changes no customer-facing document,
 *   where making the backend stop trimming would have altered live output to
 *   match a preview nobody had complained about. `renderInclusion` in
 *   `src/lib/inclusions.ts` trims the name now, and **the row's NUMBER is
 *   unchanged** — same case, corrected answer, which is what an append-only
 *   numbering is for.
 *
 *   ⚠️ **The superseded note's REASONING was sound and still governs the next
 *   one:** a shared row is cited by number in both repositories, so changing one
 *   side unilaterally voids the row rather than closing the gap. What made this
 *   one safe is that it was not unilateral — the backend was already trimming, so
 *   this is the frontend catching up, not a new opinion.
 *
 *   ⚠️ **I15 is no longer an asymmetry of TRIMMING** — both are trimmed, and the
 *   *"a name is content, a unit is grammar"* line in `inclusions.ts` is marked
 *   superseded there. It still earns its place beside I14 as the second half of
 *   "trim both, not one". The asymmetry that survives is what an EMPTY result
 *   means — see I18.
 *
 *   ⚠️ **I17 is what stops the trim widening**, and it is the only row that can.
 *   `'  MBA  fairs  '` prints `MBA  fairs`, doubled interior space intact.
 *   `.trim()` and `.replace(/\s+/g, ' ').trim()` are indistinguishable on I14 —
 *   its name has only single interior spaces — and on every other row here. Both
 *   sides call `.trim()`, so a collapse on either is a silent divergence.
 *
 *   ⚠️ **I21 pins the trim's DEFINITION**, exactly as row 21 does for the project
 *   name: `.trim()` means JavaScript's, which strips U+00A0 and every other
 *   Unicode whitespace. A hand-rolled `/^ +| +$/` on either side of this join
 *   takes the identical branch as I14 and emits a different string.
 *
 *   ⚠️ **Still unreachable through the API today**: a padded inclusion name needs
 *   an import, a script or a direct write. That is the same standing the backend
 *   gave the unit trim shortly before it turned out to matter, which is an
 *   argument for pinning these rows rather than for leaving them.
 * - **I18, I19 and I20 — an emptied NAME is not absent the way an emptied UNIT
 *   is**, and this is the one place the two trims stop behaving alike. A unit
 *   that trims to `''` is ABSENT and takes the number with it (I15's rule); a
 *   name that trims to `''` is still EMITTED, so I18 prints `(: 2 campuses)` and
 *   I19 prints `()`. **Both are what the backend emits** — `inclusionText`
 *   returns `name` or `` `${name}: …` `` with no blank-name branch either — so a
 *   defensive "treat a blank name as absent", or "skip the inclusion entirely",
 *   is a divergence dressed as a repair. I19 is also the one row that emits the
 *   empty brackets I1–I3 exist to forbid, and it is CORRECT: the segment is
 *   suppressed by an EMPTY SET, never by an empty rendering.
 *
 *   ⚠️ **I20 catches a trim applied on ONE branch only.** `renderInclusion`
 *   returns from two places — the bare name, and name-colon-unit — and the
 *   natural half-fix trims inside the template literal alone. That passes I14,
 *   I17, I18 and I21, and fails I20, whose name is padded and whose unit is
 *   `null`.
 * - **I16** — the quantity is **interpolated raw**, never formatted. The 1500 is
 *   deliberately absurd for a campaign inclusion, and that is the row's whole
 *   value: at 1 and 2 every number formatter agrees with raw interpolation, so
 *   nothing below four figures can distinguish them. This repo formats money with
 *   thousands separators (`123,456.00`), so `toLocaleString()` is an in-house
 *   habit one file away, and it prints `1,500 campuses` on a customer's document.
 *   ⚠️ Do not delete this row as unrealistic: a fixture row's job is to be
 *   executable, not plausible.
 *
 * ### The wrong implementations these rows were cut against
 *
 * The same method as rows 16–21 — enumerate the plausible WRONG composers and
 * require the table to kill each. 17 were implemented and run against every `I`
 * row; three were killed by nothing, and I14, I15 and I16 are those three
 * holes. The four-row null-unit table above is the same exercise done
 * earlier, and it is what produced I13.
 *
 * ✅ **RE-RUN 2026-08-31, when the name trim landed.** 13 wrong composers were
 * implemented against the corrected rule and run over every `I` row, alongside a
 * per-row sameness check between this composer and the backend's transcribed
 * `inclusionText` / `inclusionsSegment` / `geographySegment` / `lineSubject`.
 * **All 21 rows agree on both sides, and every one of the 13 is killed** — but
 * three of them were killed by NOTHING until I17, I20 and I21 were added, which
 * is why those three rows exist:
 *
 * - **I17** alone kills a name whose interior whitespace is collapsed.
 * - **I20** alone kills a name trimmed only inside the with-unit branch.
 * - **I21** alone kills `/^ +| +$/` standing in for `.trim()`.
 *
 * ⚠️ **The first two were invisible from I14, which is the row that looks like
 * it covers them.** A single padded name with single interior spaces cannot
 * distinguish "trim the ends" from "collapse everything", and a name that has a
 * unit cannot exercise the branch that has none. **Adding the trim without
 * adding these rows would have left the fixture agreeing with three composers
 * that disagree with the backend.**
 *
 * | plausible wrong implementation | killed by |
 * |---|---|
 * | `s === null && p === null ? name : …` — the unitless-master branch | I9, I13 |
 * | `(q === 1 ? s : p) ?? s ?? p` — symmetric coalesce | I9, I13 |
 * | `q === 1 && s !== null ? s : p` — "prefer the singular" | **I13 alone** |
 * | the NUMBER printed in the no-unit case | I8, I9, I10, I13 |
 * | a client-side pluraliser instead of `unitPlural` | I5, I6, I7, I9, I10, I11, I12 |
 * | a pluraliser only as a FALLBACK for a null `unitPlural` | **I9 alone** |
 * | `name` left UNTRIMMED | **I14, I17, I18, I20, I21** — trimming the name is CORRECT, on both sides; see I14's note |
 * | units left UNTRIMMED (`!== null`) | **I15** — trimming the unit is CORRECT, on both sides |
 * | `name` trimmed only inside the WITH-UNIT branch | **I20 alone** |
 * | `name` trimmed only inside the BARE branch | I14, I17, I18, I21 |
 * | `name` interior whitespace COLLAPSED (`replace(/\s+/g, ' ')`) | **I17 alone** |
 * | `name` trimmed with `/^ +\| +$/` rather than `.trim()` | **I21 alone** |
 * | a blank `name` treated as ABSENT, or its inclusion skipped | **I18** |
 * | `name` TITLE-CASED | I14, I17, I21 |
 * | `quantity` through `toLocaleString()` | **I16** |
 * | `' - '` rather than `': '` after the name | I4, I5, I6, I7, I10, I11, I12 |
 * | inclusions joined with `', '` rather than `'; '` | I6, I7, I10 |
 * | inclusions SORTED by name | **I7 alone** |
 * | empty brackets when the line carries none | I1, I2, I3 |
 * | an `Includes: ` label inside the brackets | every row with an inclusion |
 * | the segment placed BEFORE the cities | I5, I6, I8, I11, I12 |
 * | the boundary moved to `q <= 1` instead of `q === 1` | **nothing — see below** |
 *
 * ⚠️ **`q <= 1` is killed by nothing, and no row should be added for it.** It can
 * only differ from `q === 1` at a quantity of zero or less, and that quantity is
 * unreachable from either side: `quantity` is `NOT NULL DEFAULT 1` with a
 * `CHECK (quantity >= 1)`, the write schema refuses zero, and on this side
 * {@link effectiveInclusionQuantity} maps every unusable box to `1`. A fixture
 * row asserting an output for a value neither database can hold would pin a
 * string neither composer can ever emit — and the first person to "correct" it
 * would be arguing about an input that does not exist. Record it as *unreachable*
 * rather than as *uncovered*; those are different findings, and only one of them
 * is a hole.
 *
 * ### A frontend-only row, for {@link effectiveInclusionQuantity}
 *
 * The backend has no equivalent — it reads an integer column. This pins the
 * string a quantity box holds against the number the line will be STORED with:
 *
 * ```
 * ''      -> 1    // "omit the key"; the server's default. NOT 0
 * '1'     -> 1
 * '2'     -> 2
 * '0'     -> 1    // refused by zod AND a CHECK, so the key is omitted -> 1
 * 'abc'   -> 1    // unparseable; the key is omitted -> 1
 * ```
 *
 * `''` is the row that matters. `Number('')` is `0`, which is neither 1 nor
 * invalid — it selects `unitPlural` and prints **`0 campuses`** in a preview of a
 * customer's document, for a line that will be stored as one campus.
 *
 * ## What is NOT pinned here, deliberately
 *
 * An empty `productName`. It is passed through unguarded and yields
 * `' — Mumbai'` (see the composer's doc), and that is a call-site fault rather
 * than a composition rule — pinning it here would invite someone to "fix" it into
 * a guard this module has explicitly declined to add.
 */
